import { Field, Form, Formik, FormikHelpers } from 'formik';
import React from 'react';
import { merchantAgreementTerms } from 'src/common';
import { MiniAppFocusWrapper, OtpInput } from 'src/components';
import { Button, Modal } from 'src/features/common';
import {
  ButtonParamType,
  DefaultButtonStyle,
} from 'src/features/common/components/button/revamp-button';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import { requestSmsOtp, validateOtp } from 'src/services/otp-service';
import toastMessageServiceSingleton from 'src/services/toast-message.service';

interface Props {
  visible: boolean;
  onClose: () => void;
  onCancel?: () => void;
  onContinue?: () => void;
  onResend?: () => void;
  onIncorrectOtp?: () => void;
  onOtpExpired?: () => void;
  mobileNumber?: string;
}

interface IInitValues {
  otp: string;
  isOtpExpired: boolean;
  isRetryLimitExceeded: boolean;
  submitAction: string;
}

export function OtpModal({
  visible,
  onClose,
  onCancel,
  onContinue,
  onResend,
  onIncorrectOtp,
  onOtpExpired,
  mobileNumber,
}: Props) {
  const otpLength = 4;
  const otpTriesMax = 3;
  const [otpTries, setOtpTries] = React.useState(0);
  const otpTriesExceeded = () => otpTries >= otpTriesMax;
  const handleTechnicalError = useTechnicalErrorHandler();

  const initValues: IInitValues = {
    otp: '',
    isOtpExpired: false,
    isRetryLimitExceeded: otpTriesExceeded(),
    submitAction: 'validate',
  };

  const getInitialErrors = () => {
    const errors: { otp?: string } = {};

    if (otpTriesExceeded()) {
      errors.otp = 'You’ve exceeded the OTP attempts. Please try again later';
    }

    return errors;
  };

  const submitForm = async (values: IInitValues, helpers: FormikHelpers<IInitValues>) => {
    switch (values.submitAction) {
      case 'validate':
        await submitFormValidate(values, helpers);
        break;
      case 'request':
        await submitFormRequest(values, helpers);
        break;
    }
  };

  const submitFormRequest = async (values: IInitValues, helpers: FormikHelpers<IInitValues>) => {
    const { setSubmitting, setFieldError, resetForm } = helpers;
    resetForm();
    setOtpTries(0);

    setSubmitting(true);
    const { error } = await requestSmsOtp(mobileNumber);

    let displayError;

    switch (error) {
      case null:
        toastMessageServiceSingleton.addToast({
          message: `We resent the customer an OTP via SMS`,
          type: 'info',
        });
        break;
      case 'Maximum retry limit exceeded. Locked for 24 hours.':
        displayError = `You’ve exceeded the OTP attempts. Please try again later`;
        break;
      default:
        // @ts-ignore
        handleTechnicalError(error, null, null, null);
        break;
    }

    if (displayError) {
      setFieldError('otp', displayError);
    }

    setSubmitting(false);
  };

  const submitFormValidate = async (values: IInitValues, helpers: FormikHelpers<IInitValues>) => {
    const { setSubmitting, setFieldError, setFieldValue } = helpers;

    setSubmitting(true);
    const { error } = await validateOtp(mobileNumber ?? '', values.otp);

    let displayError;

    switch (error) {
      case null:
        break;
      case 'Maximum retry limit exceeded. Locked for 24 hours.':
        displayError = `You’ve exceeded the OTP attempts. Please try again later`;
        break;
      case 'Validation failed due to incorrect OTP':
        onIncorrectOtp && onIncorrectOtp();

        const otpRetriesCurrent = otpTries + 1;
        setOtpTries(otpRetriesCurrent);

        if (otpRetriesCurrent >= otpTriesMax) {
          displayError = 'You’ve exceeded the OTP attempts. Please try again later';
        } else {
          displayError = `Incorrect OTP - Try again ${otpRetriesCurrent}/${otpTriesMax}`;
        }
        break;
      case 'No OTP found. Generate OTP first':
        onOtpExpired && onOtpExpired();

        setFieldValue('isOtpExpired', false, false);
        displayError = 'Your OTP has expired. Please request a new OTP';
        break;
      default:
        // @ts-ignore
        handleTechnicalError(
          'unrecognized error received in response to OTP get request',
          null,
          null,
          null,
        );
        break;
    }

    if (displayError) {
      setFieldError('otp', displayError);
    }

    setSubmitting(false);

    if (!error) {
      sessionStorage.setItem('verified_mobile_number', 'true');
      onClose();
    }
  };

  return (
    <Modal visible={visible} size="small">
      <Formik initialErrors={getInitialErrors()} initialValues={initValues} onSubmit={submitForm}>
        {({ values, submitForm, setFieldValue, errors }) => {
          const isValid = !errors.otp && values.otp !== '' && values.otp.length === otpLength;

          const handleValidateOtp = async () => {
            setFieldValue('submitAction', 'validate');
            submitForm();
          };

          const handleRequestOtp = async () => {
            if (!otpTriesExceeded()) {
              setFieldValue('submitAction', 'request');
              submitForm();
            }
          };

          return (
            <Form>
              <section className="grid gap-10 text-center text-grey-600">
                <div className="md:px-5">
                  <h1 className="text-base md:text-4xl">We’ve sent the customer an OTP</h1>
                  <p className="text-xs mt-10 !font-normal md:text-2xl text-grey-400">
                    Please enter the 4 digit verification code, to confirm that the customer has
                    accepted the{' '}
                    <a
                      className="text-red-600 underline cursor-pointer"
                      href={merchantAgreementTerms}
                      target="_blank"
                      rel="noreferrer"
                    >
                      terms and conditions
                    </a>
                  </p>
                </div>
                <div>
                  <Field
                    name="otp"
                    component={MiniAppFocusWrapper}
                    innerComponent={OtpInput}
                    key={otpTries}
                    shouldAutoFocus={true}
                    isDisabled={otpTriesExceeded() || values.isOtpExpired}
                    numInputs={otpLength}
                    autoFocus
                  />
                </div>

                <div className="flex items-center gap-3">
                  <Button
                    type={'button'}
                    buttonStyle={DefaultButtonStyle()
                      .hasSecondaryAction()
                      .isMediumSize()
                      .hasLightMode()
                      .build()}
                    buttonParams={{
                      type: ButtonParamType.NormalButton,
                      onClick: () => {
                        onCancel && onCancel();
                        onClose();
                      },
                    }}
                    className="w-56 !text-black"
                    automationid={`DA_Continue_Button`}
                  >
                    Cancel
                  </Button>

                  <Button
                    type={'button'}
                    disabled={!isValid}
                    buttonStyle={DefaultButtonStyle()
                      .hasPrimaryAction()
                      .isMediumSize()
                      .hasLightMode()
                      .build()}
                    buttonParams={{
                      type: ButtonParamType.NormalButton,
                      onClick: () => {
                        onContinue && onContinue();
                        handleValidateOtp();
                      },
                    }}
                    className="w-56"
                    automationid={`DA_Continue_Button`}
                  >
                    Continue
                  </Button>
                </div>

                <div className="flex justify-center text-base">
                  <span className="text-center">Didn't receive an OTP?</span>
                  &nbsp;
                  <span className="line-break">
                    <br />
                  </span>
                  <span
                    className={`font-medium ${
                      otpTriesExceeded()
                        ? 'cursor-not-allowed text-grey-300'
                        : 'text-red-600 cursor-pointer'
                    }`}
                    onClick={() => {
                      onResend && onResend();
                      handleRequestOtp();
                    }}
                  >
                    Resend
                  </span>
                </div>
              </section>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
}
